import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import {LoadingButton} from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
} from '@mui/material';
import {useSnackbar} from 'notistack';
import {useState} from 'react';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {MapTilingJob} from '../../../interfaces/Map';
import AccessControl from '../../common/AccessControl';
import {CloseSnackbarAction} from '../../common/CloseSnackbarButton';
import SnackbarMessages from '../../common/SnackbarMessages';

interface MapTilingJobActionsProps {
  job: MapTilingJob;
  onChanged?: () => void;
}

export const MapTilingJobActions: React.FC<MapTilingJobActionsProps> = ({
  job,
  onChanged,
}) => {
  const {enqueueSnackbar} = useSnackbar();

  const [isConfirmOpened, setIsConfirmOpened] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (job.status !== 'failed') {
    return null;
  }

  const reportError = (error: unknown) => {
    const messages = getMessagesFromApiError(error, 'STRA_0c93a17b');
    enqueueSnackbar(<SnackbarMessages messages={messages} />, {
      variant: 'error',
      action: CloseSnackbarAction,
    });
  };

  const retry = async () => {
    setIsSubmitting(true);

    try {
      await API.post(`${apiBaseUrl}/map/tiling-job/${job.id}/retry`);
      enqueueSnackbar('Tiling restarted', {
        variant: 'success',
        action: CloseSnackbarAction,
      });
      onChanged?.();
    } catch (error) {
      reportError(error);
    }

    setIsSubmitting(false);
  };

  const remove = async () => {
    setIsSubmitting(true);

    try {
      await API.delete(`${apiBaseUrl}/map/tiling-job/${job.id}`);
      enqueueSnackbar('Tiling job removed', {
        variant: 'success',
        action: CloseSnackbarAction,
      });
      setIsConfirmOpened(false);
      onChanged?.();
    } catch (error) {
      reportError(error);
    }

    setIsSubmitting(false);
  };

  return (
    <>
      <AccessControl permissions={['post::/map/tiling-job/:id/retry']}>
        <Tooltip title="Retry tiling">
          <span>
            <IconButton
              size="small"
              color="primary"
              aria-label="Retry tiling"
              disabled={isSubmitting}
              onClick={retry}
            >
              <ReplayIcon />
            </IconButton>
          </span>
        </Tooltip>
      </AccessControl>

      <AccessControl permissions={['delete::/map/tiling-job/:id']}>
        <Tooltip title="Remove from the list">
          <span>
            <IconButton
              size="small"
              color="error"
              aria-label="Remove tiling job"
              disabled={isSubmitting}
              onClick={() => setIsConfirmOpened(true)}
            >
              <DeleteOutlineOutlinedIcon />
            </IconButton>
          </span>
        </Tooltip>
      </AccessControl>

      <Dialog open={isConfirmOpened} onClose={() => setIsConfirmOpened(false)}>
        <DialogTitle>Remove tiling job</DialogTitle>

        <DialogContent>
          <DialogContentText>
            {`"${job.name}" and its uploaded image will be removed. The map itself is not affected.`}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setIsConfirmOpened(false)}>Cancel</Button>

          <LoadingButton
            color="error"
            variant="contained"
            loading={isSubmitting}
            onClick={remove}
          >
            Remove
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
